import { Button, IconButton } from '@dropbox/dig-components/buttons';
import { Text, Title } from '@dropbox/dig-components/typography';
import {
  FormHelperText,
  FormLabel,
  FormRow,
} from '@dropbox/dig-components/form_row';
import { TextInput } from '@dropbox/dig-components/text_fields';
import { Box } from '@dropbox/dig-foundations';
import { UIIcon } from '@dropbox/dig-icons';
import {
  FailLine,
  HideLine,
  InfoLine,
  ShowLine,
} from '@dropbox/dig-icons/assets';
import { SignWordmark, WordmarkLogo } from '@dropbox/dig-logos';
import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styles from 'signer-app/signer-experience/access-code.module.css';
import { Footer } from 'signer-app/signer-experience/footer';
import { AccessCodeResponse } from 'signer-app/signer-experience/use-auto-save-signer-experience';
import { WhiteLabelThemeProvider } from 'signer-app/white-labeling/theme-provider';
import useIOSViewportFix from './use-ios-viewport-fix';

const messages = defineMessages({
  invalidCode: {
    id: 'e2eed22dc40231828355bc83f1fc5483d2e4c08a97f679b938c2a9dd732527c9',
    defaultMessage:
      'Invalid code: {numAttemptsLeft, number} attempts remaining',
    description: 'Error message for invalid access code',
  },
  hidePassword: {
    id: '6ea66a547ea83487afe68029a4c104028f152ed70de7e9fdf9dc8ee5b07d10e5',
    defaultMessage: 'Hide password',
    description: 'Aria label for hide password button',
  },
  showPassword: {
    id: 'b8cce5c9e128f7d355e4aab2b08c32b535f7146bf6709166afdcccfd3d3fcd7d',
    defaultMessage: 'Show password',
    description: 'Aria label for show password button',
  },
  maxRetries: {
    id: 'signer.enterCode.error.maxAttemptsText',
    description:
      'error message in signer flow, informing singer have maxed out the attemps to put access code',
    defaultMessage: 'You have reached the maximum number of attempts.',
  },
  maxRetriesV2: {
    id: 'ad3653e97c12643bd054575d768be43ba5f843b1f64ecc0d1120f2a96811004d',
    description:
      'error message in signer flow, informs signer they have maxed out the attempts to put access code and they need to contact sender',
    defaultMessage:
      "You've hit the limit of attempts. Ask the sender to restore access",
  },
  incorrectCode: {
    id: '8d39bf2efeac6a87b66e6209b02586e902d047c0cb60ab09ec57027098191319',
    description: 'Error message for invalid access code',
    defaultMessage:
      'Incorrect code. {numAttemptsLeft, number} attempts remaining',
  },
});

export interface AccessCodeProps {
  error: AccessCodeResponse | null;
  isFetching: boolean;
  onSubmit: (accessCode: string) => void;
  isEmbedded: boolean;
  numAttemptsLeft: number;
}

export function AccessCode({
  error,
  onSubmit,
  isEmbedded,
  isFetching,
  numAttemptsLeft,
}: AccessCodeProps) {
  const intl = useIntl();
  const [viewPass, toggleViewPass] = React.useReducer((x) => !x, false);

  const errorMessage = React.useMemo(() => {
    switch (error) {
      case 'invalid-access-code':
        return intl.formatMessage(messages.incorrectCode, {
          numAttemptsLeft,
        });
      case 'too-many-retries':
        return intl.formatMessage(messages.maxRetriesV2);
      default:
        return null;
    }
  }, [intl, error, numAttemptsLeft]);

  useIOSViewportFix('access-code');

  return (
    <>
      <WhiteLabelThemeProvider mode="dark" colors="reset">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          padding="Micro Small"
          backgroundColor="Background Subtle"
        >
          <Box color="Text Base" display="flex">
            <WordmarkLogo size={32} src={SignWordmark} />
          </Box>
        </Box>
      </WhiteLabelThemeProvider>
      <WhiteLabelThemeProvider mode="bright" colors="reset">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          rowGap="Macro Medium"
          paddingX="Macro Small"
          className={styles.container}
          id="access-code"
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            rowGap="Micro Small"
            className={styles.content}
          >
            <Title weightVariant="emphasized">
              <FormattedMessage
                id="0f2cc781dade071c8ff93dfd1a0b69a986d107ac7170159e810ba8b2e9f944f0"
                defaultMessage="Enter access code"
                description="Title for access code form"
              />
            </Title>
            <Text
              color="subtle"
              variant="label"
              className={styles.instructionText}
            >
              <FormattedMessage
                id="7a7ebd1ee84b8461a859e15390f90c12d068aef2a031c68f80aa584c2732cfd7"
                defaultMessage="To access this document, enter the code provided by the sender. Contact the sender for more information."
                description="Instructions for entering access code"
              />
            </Text>
          </Box>

          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.currentTarget);
              const code = formData.get('accessCode')?.toString() ?? '';

              onSubmit(code);
            }}
            className={styles.content}
          >
            <FormRow>
              <FormLabel htmlFor="accessCode">
                <FormattedMessage
                  id="7742974672e5a0d2898247d952fbb8a4efb472aa87bd2bceb36538f62c3bc72f"
                  defaultMessage="Access code"
                  description="Label for access code input"
                />
              </FormLabel>
              <TextInput
                name="accessCode"
                id="accessCode"
                aria-describedby={!errorMessage ? 'case-sensitive' : undefined}
                aria-errormessage={errorMessage ? 'error-msg' : undefined}
                autoFocus
                disabled={isFetching || error === 'too-many-retries'}
                isInvalid={!!errorMessage}
                type={viewPass ? 'text' : 'password'}
                size="large"
                withRightAccessory={
                  <IconButton
                    variant="transparent"
                    size="small"
                    onClick={toggleViewPass}
                    type="button"
                  >
                    <UIIcon
                      size="small"
                      src={viewPass ? HideLine : ShowLine}
                      aria-label={intl.formatMessage(
                        viewPass
                          ? messages.hidePassword
                          : messages.showPassword,
                      )}
                    />
                  </IconButton>
                }
              />
              {errorMessage ? (
                <FormHelperText id="error-msg" variant="alert">
                  <UIIcon src={FailLine} size="small" /> {errorMessage}
                </FormHelperText>
              ) : (
                <FormHelperText id="case-sensitive">
                  <UIIcon src={InfoLine} size="small" />
                  <FormattedMessage
                    id="07b62daefd18bc1a417272414de2e6a879d099a854e2136af226c0b95e4c82ac"
                    defaultMessage="The access code is case sensitive."
                    description="Helper text for access code input"
                  />
                </FormHelperText>
              )}
            </FormRow>
            <Button
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              isLoading={isFetching}
              disabled={error === 'too-many-retries'}
            >
              <FormattedMessage
                id="6a02f9921e0e52b306d9a720872dbc94a46d05a32ddff441040953abdd1bddcf"
                defaultMessage="Continue"
                description="Button text for submitting access code"
              />
            </Button>
          </form>
        </Box>
        {!isEmbedded && <Footer />}
      </WhiteLabelThemeProvider>
    </>
  );
}
